import { Head, useForm, Link, router } from "@inertiajs/react"
import { ArrowLeft, Camera, X } from "lucide-react"
import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import { samplesPath, editSamplePath, samplePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Combobox } from "@/components/ui/combobox"

const processOptions = [
  { value: "natural", label: "Natural" },
  { value: "washed", label: "Lavado" },
  { value: "honey", label: "Honey" },
]

const varietalOptions = [
  { value: "typica", label: "Typica" },
  { value: "caturra", label: "Caturra" },
  { value: "catuai", label: "Catuaí" },
  { value: "gesha", label: "Gesha" },
  { value: "bourbon_amarillo", label: "Bourbon Amarillo" },
  { value: "bourbon_rosado", label: "Bourbon Rosado" },
  { value: "solok", label: "Solok" },
]

interface Tasting {
  id: number
  title: string
}

interface Sample {
  id: number
  name: string
  picture: string
  picture_url: string | null
  description: string
  origin: string
  land: string
  producer: string
  process: string
  variety: string
  tasting?: Tasting
}

interface Props {
  sample: Sample
  tastings: Tasting[]
}

export default function EditStandaloneSample({ sample, tastings }: Props) {
  const { t } = useTranslation()
  const [picturePreview, setPicturePreview] = useState<string | null>(
    sample.picture_url,
  )
  const [newPicture, setNewPicture] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("samples.title"),
      href: samplesPath(),
    },
    {
      title: sample.name,
      href: samplePath(sample.id),
    },
    {
      title: t("samples.edit.page_title"),
      href: editSamplePath(sample.id),
    },
  ]

  const { data, setData, processing, errors } = useForm({
    name: sample.name,
    description: sample.description || "",
    origin: sample.origin || "",
    land: sample.land || "",
    producer: sample.producer || "",
    process: sample.process?.toString() || "",
    variety: sample.variety?.toString() || "",
    tasting_id: sample.tasting?.id?.toString() || "",
  })

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setNewPicture(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPicturePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const removePicture = () => {
    setNewPicture(null)
    setPicturePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData()
    formData.append("sample[name]", data.name)
    formData.append("sample[description]", data.description)
    formData.append("sample[origin]", data.origin)
    formData.append("sample[land]", data.land)
    formData.append("sample[producer]", data.producer)
    formData.append("sample[process]", data.process)
    formData.append("sample[variety]", data.variety)
    if (data.tasting_id) {
      formData.append("sample[tasting_id]", data.tasting_id)
    }
    if (newPicture) {
      formData.append("sample[picture]", newPicture)
    }
    router.post(samplePath(sample.id), formData as any, {
      headers: { "X-HTTP-Method-Override": "PUT" },
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Editar ${sample.name}`} />

      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Link href={samplesPath()}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <CardTitle>{t("samples.edit.title")}</CardTitle>
                <CardDescription>
                  {t("samples.edit.description")}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t("samples.edit.fields.name_required")}
                  </Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    placeholder={t("samples.edit.fields.name_placeholder")}
                    required
                  />
                  {errors.name && (
                    <p className="text-destructive text-sm">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tasting_id">
                    {t("samples.edit.fields.cupping")}
                  </Label>
                  <Select
                    value={data.tasting_id || "none"}
                    onValueChange={(value) =>
                      setData("tasting_id", value === "none" ? "" : value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "samples.edit.fields.cupping_placeholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {t("samples.edit.fields.no_cupping")}
                      </SelectItem>
                      {tastings?.map((tasting) => (
                        <SelectItem
                          key={tasting.id}
                          value={tasting.id.toString()}
                        >
                          {tasting.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.tasting_id && (
                    <p className="text-destructive text-sm">
                      {errors.tasting_id}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>{t("samples.edit.fields.image")}</Label>
                  <div className="flex items-center gap-4">
                    {picturePreview ? (
                      <div className="relative">
                        <img
                          src={picturePreview}
                          alt="Preview"
                          className="h-24 w-24 rounded-lg border object-cover"
                        />
                        <button
                          type="button"
                          onClick={removePicture}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90 absolute -top-2 -right-2 rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="picture"
                        className="border-muted-foreground/25 hover:border-primary/50 flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors"
                      >
                        <Camera className="text-muted-foreground h-8 w-8" />
                        <span className="text-muted-foreground mt-1 text-xs">
                          {t("samples.edit.fields.upload_photo")}
                        </span>
                      </label>
                    )}
                    <input
                      ref={fileInputRef}
                      id="picture"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePictureChange}
                      className="hidden"
                    />
                  </div>
                  {(errors as any).picture && (
                    <p className="text-destructive text-sm">
                      {(errors as any).picture}
                    </p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">
                    {t("samples.edit.fields.description")}
                  </Label>
                  <Input
                    id="description"
                    value={data.description}
                    onChange={(e) => setData("description", e.target.value)}
                    placeholder={t(
                      "samples.edit.fields.description_placeholder",
                    )}
                  />
                  {errors.description && (
                    <p className="text-destructive text-sm">
                      {errors.description}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="origin">
                    {t("samples.edit.fields.origin")}
                  </Label>
                  <Input
                    id="origin"
                    value={data.origin}
                    onChange={(e) => setData("origin", e.target.value)}
                    placeholder={t("samples.edit.fields.origin_placeholder")}
                  />
                  {errors.origin && (
                    <p className="text-destructive text-sm">{errors.origin}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="land">{t("samples.edit.fields.land")}</Label>
                  <Input
                    id="land"
                    value={data.land}
                    onChange={(e) => setData("land", e.target.value)}
                    placeholder={t("samples.edit.fields.land_placeholder")}
                  />
                  {errors.land && (
                    <p className="text-destructive text-sm">{errors.land}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="producer">
                    {t("samples.edit.fields.producer")}
                  </Label>
                  <Input
                    id="producer"
                    value={data.producer}
                    onChange={(e) => setData("producer", e.target.value)}
                    placeholder={t("samples.edit.fields.producer_placeholder")}
                  />
                  {errors.producer && (
                    <p className="text-destructive text-sm">
                      {errors.producer}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="process">
                    {t("samples.edit.fields.process")}
                  </Label>
                  <Select
                    value={data.process}
                    onValueChange={(value) => setData("process", value)}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "samples.edit.fields.process_placeholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {processOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.process && (
                    <p className="text-destructive text-sm">{errors.process}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="variety">
                    {t("samples.edit.fields.variety")}
                  </Label>
                  <Combobox
                    options={varietalOptions}
                    value={data.variety}
                    onChange={(value) => setData("variety", value)}
                    placeholder={t("samples.edit.fields.variety_placeholder")}
                  />
                  {errors.variety && (
                    <p className="text-destructive text-sm">{errors.variety}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={processing}>
                  {t("samples.edit.actions.save")}
                </Button>
                <Link href={samplesPath()}>
                  <Button type="button" variant="outline">
                    {t("samples.edit.actions.cancel")}
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
